import type { CarouselApi } from "@au-schein-de/au-ui-library";
import {
	Card,
	Carousel,
	ChevronLeftIcon,
	ChevronRightIcon,
	cn,
} from "@au-schein-de/au-ui-library";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ExpertReview, ExpertReviewSection } from "./expert-review.types";
import { ExpertReviewBody } from "./expert-review-body.component";
import { ExpertReviewHeader } from "./expert-review-header.component";
import { ExpertReviewLearnMoreDialog } from "./expert-review-learn-more-dialog.component";

type ExpertReviewProps = {
	review: ExpertReview;
};

export function ExpertReviewComponent({ review }: ExpertReviewProps) {
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);
	const { t } = useTranslation("product");

	const sections = [
		{ category: "Overall Experience", text: review.reviewConclusion },
		{ category: "Appearance", text: review.reviewAppearance },
		{ category: "Aroma", text: review.reviewAroma },
		{ category: "Flavor", text: review.reviewFlavor },
		{ category: "Effects", text: review.reviewEffects },
	].filter((s): s is ExpertReviewSection => !!s.text);

	useEffect(() => {
		if (!api) return;

		const handleSelect = () => {
			setCurrent(api.selectedScrollSnap());
		};

		handleSelect();
		api.on("select", handleSelect);

		return () => {
			api.off("select", handleSelect);
		};
	}, [api]);

	const scrollTo = useCallback(
		(index: number) => {
			api?.scrollTo(index);
		},
		[api],
	);

	return (
		<Card.Root className="w-full p-6 md:p-8">
			<ExpertReviewHeader
				overallRating={review.overallRating}
				reviewerName={review.reviewerName}
			/>

			<div className="mt-6 md:mt-8" data-testid="expert-review-carousel">
				<Carousel.Root
					opts={{ loop: true, align: "start" }}
					className="w-full"
					setApi={setApi}
				>
					<Carousel.Content className="-ml-2.5">
						{sections.map((section) => (
							<Carousel.Item
								key={section.category}
								className="pl-2.5 basis-auto"
								full={false}
							>
								<ExpertReviewBody section={section} />
							</Carousel.Item>
						))}
					</Carousel.Content>

					<div className="flex justify-center gap-2 mt-6">
						{sections.map((section, index) => (
							<button
								key={section.category}
								type="button"
								onClick={() => scrollTo(index)}
								className={cn(
									"w-2 h-2 rounded-full transition-colors cursor-pointer",
									index === current ? "bg-[#FC7E55]" : "bg-gray-200",
								)}
								aria-label={`Go to section ${index + 1}`}
								data-testid="carousel-navigation-dot"
							/>
						))}
					</div>
				</Carousel.Root>

				<div className="flex flex-col md:flex-row justify-center md:justify-between items-center mt-6 gap-4">
					<ExpertReviewLearnMoreDialog />
					<div className="hidden md:flex space-x-2">
						<button
							type="button"
							onClick={() => api?.scrollPrev()}
							className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center cursor-pointer"
							data-testid="carousel-previous-button"
							aria-label="Previous section"
						>
							<ChevronLeftIcon.Raw className="text-gray-700 w-6 h-6 -ml-[3px]" />
						</button>
						<button
							type="button"
							onClick={() => api?.scrollNext()}
							className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center cursor-pointer"
							data-testid="carousel-next-button"
							aria-label="Next section"
						>
							<ChevronRightIcon.Raw className="text-gray-700 w-6 h-6 ml-[3px]" />
						</button>
					</div>
				</div>
			</div>
		</Card.Root>
	);
}
